import React, { useEffect, useRef, useState } from "react";
import { sendDemoEnquiry } from "../utils/email";

const DemoFormModal = ({ show, onClose }) => {
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const nameInputRef = useRef(null);
  const previousFocusedElementRef = useRef(null);

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    previousFocusedElementRef.current = document.activeElement;
    nameInputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) {
        return;
      }

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusedElementRef.current?.focus?.();
    };
  }, [onClose, show]);

  const validate = () => {
    const nextErrors = {};

    if (!name.trim()) {
      nextErrors.name = "Name is required.";
    }

    if (!company.trim()) {
      nextErrors.company = "Company is required.";
    }

    if (!contact.trim()) {
      nextErrors.contact = "Contact number is required.";
    }

    if (!message.trim()) {
      nextErrors.message = "Message is required.";
    } else if (message.trim().length < 10) {
      nextErrors.message = "Message should be at least 10 characters.";
    }

    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      await sendDemoEnquiry({ name, company, contact, message });
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setName("");
        setCompany("");
        setContact("");
        setMessage("");
        setValidationErrors({});
        onClose();
      }, 2000);
    } catch (err) {
      setError("Failed to send. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const handleOverlayClick = (event) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <div className="demo-modal-overlay" ref={overlayRef} onMouseDown={handleOverlayClick}>
      <div
        className="demo-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-demo-title"
      >
        <button className="demo-modal-close" onClick={onClose} aria-label="Close schedule demo modal">&times;</button>
        <h2 id="schedule-demo-title">Schedule Demo</h2>
        <form className="demo-form" onSubmit={handleSubmit} noValidate>
          <label htmlFor="demo_name">Name</label>
          <input
            id="demo_name"
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={Boolean(validationErrors.name)}
            aria-describedby={validationErrors.name ? "demo_name_error" : undefined}
          />
          {validationErrors.name && (
            <div id="demo_name_error" className="form-field-error" role="alert">
              {validationErrors.name}
            </div>
          )}

          <label htmlFor="demo_company">Company</label>
          <input
            id="demo_company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            aria-invalid={Boolean(validationErrors.company)}
            aria-describedby={validationErrors.company ? "demo_company_error" : undefined}
          />
          {validationErrors.company && (
            <div id="demo_company_error" className="form-field-error" role="alert">
              {validationErrors.company}
            </div>
          )}

          <label htmlFor="demo_contact">Contact Number</label>
          <input
            id="demo_contact"
            type="tel"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            aria-invalid={Boolean(validationErrors.contact)}
            aria-describedby={validationErrors.contact ? "demo_contact_error" : undefined}
          />
          {validationErrors.contact && (
            <div id="demo_contact_error" className="form-field-error" role="alert">
              {validationErrors.contact}
            </div>
          )}

          <label htmlFor="demo_message">Message</label>
          <textarea
            id="demo_message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            aria-invalid={Boolean(validationErrors.message)}
            aria-describedby={validationErrors.message ? "demo_message_error" : undefined}
          />
          {validationErrors.message && (
            <div id="demo_message_error" className="form-field-error" role="alert">
              {validationErrors.message}
            </div>
          )}

          <button type="submit" className="demo-send-btn" disabled={loading}>{loading ? "Sending..." : "Submit"}</button>
        </form>
        {sent && <div className="demo-sent-msg" role="status" aria-live="polite">Thank you! Your enquiry has been sent.</div>}
        {error && <div className="demo-sent-msg demo-sent-msg-error" role="alert">{error}</div>}
      </div>
    </div>
  );
};

export default DemoFormModal;
